import { SUPPORT_EMAIL } from "constants/app.constants";

import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";
import useUserModules from "hooks/useUserModules";

export const NO_GEO_SOURCE_MESSAGE = `Your organization has no Geology or 3D Geo Model subscription and no private geo models, so there is no geo source to calculate against. Contact ${SUPPORT_EMAIL} to enable a geo model.`;

/**
 * EVA-150: whether the org has any geo source it may calculate against.
 *
 * There are exactly two: "EVA Research" (the McDaniel model) and the org's own private/imported
 * models. EVA Research is gated per play by TWO subscription tiers — Geology unlocks the XDA,
 * spacing and parent-child columns, and 3D Geo Model additionally unlocks the sampled-3D columns.
 * So Geology alone is enough to make a job worth creating, and the check must not require 3D.
 *
 * Only with neither tier and no private model would every job be gated to nothing, and that is
 * when the Geo Model Calculation and Geo Model Simulation pages block creation up front rather
 * than opening a modal that can only fail.
 *
 * Shared by both pages so the rule and its message can't drift apart. Purely a UX guard — the
 * server-side play filter in the workers is what actually protects the gated model.
 */
export function useCanCreateGeoJob(): { canCreate: boolean; isLoading: boolean } {
  const { hasGeology, has3dGeoModel } = useUserModules();
  const { data: modelSources, isLoading, isError } = usePlayTo3DDataPrivateSources();

  return {
    // `isLoading` is deliberately NOT in this list: treating "still loading" as allowed let a
    // fully unentitled org straight through whenever the request had not resolved, which is the
    // hole this guard exists to close. Callers surface the pending state instead — a disabled
    // button beats a wrong answer in either direction.
    //
    // `isError` IS in the list: on a failed request react-query gives isLoading false and data
    // undefined, so an org that genuinely has private models would otherwise be told it has no
    // subscription and no geo model, which is wrong on both counts. Unknown must not read as none.
    canCreate: hasGeology || has3dGeoModel || isError || (modelSources?.length ?? 0) > 0,
    isLoading
  };
}
